import './styles.scss';
import { formatToLocalTime } from '#root/services/utils';
import { getEnumLabelByValue } from "#root/utils/enums";
import { MEAL_ACCESS_CONST, RESPONDER_ROLES_CONST, RESPONDER_STATUS_CONST, USR_TEAMS } from "#root/utils/responders";
import { Responder } from "#root/queries/responders";
import LazyImage from "#root/components/LazyImage";
import { Checkbox } from '@fluentui/react-components';
import React from 'react';

const truncateText = (text: string, maxLength: number = 35): string => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
};

interface ResponderCardProps {
    responder: Responder;
    actions?: React.ReactNode;
    handleSelectionChange: (event: any, data: any) => void;
    responderChecked: boolean;
    allowSelect?: boolean;
    showDeploymentFields?: boolean;
}

const ResponderCard: React.FC<ResponderCardProps> = ({ 
    responder, 
    actions, 
    handleSelectionChange, 
    responderChecked,
    allowSelect = true,
    showDeploymentFields = false
}) => {
    return (
        <div className="large-card responder-card" data-responder-id={responder.id}>
            <div className="large-card__content">
                <div className="large-card__header">
                    <div className="large-card__info">
                        <div className='large-card__subtitle'>
                            {allowSelect && (
                                <Checkbox
                                    checked={responderChecked}
                                    onChange={(e, _) => {
                                        handleSelectionChange(e, {
                                            selectedItems: [responder.id]
                                        });
                                    }}
                                />
                            )}
                            <span className="large-card__label">Created at: </span>
                            <span className="large-card__value">{formatToLocalTime(responder.createdDate, 'long')}</span>
                        </div>
                        <span className="large-card__title">Incident Name: {responder.incidentName}</span>
                    </div>
                </div>
                <div className={`large-card__details ${showDeploymentFields ? 'show-deployment-fields' : ''}`}>
                    <div>
                        <span className="large-card__label">First Name: </span>
                        <span className="large-card__value" title={responder.firstName}>{truncateText(responder.firstName)}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Last Name: </span>
                        <span className="large-card__value" title={responder.lastName}>{truncateText(responder.lastName)}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Nickname: </span>
                        <span className="large-card__value" title={responder.nickname}>{truncateText(responder.nickname)}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Department/Agency: </span>
                        <span className="large-card__value" title={responder.departmentOrAgency}>{truncateText(responder.departmentOrAgency)}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Email: </span>
                        <span className="large-card__value">{responder.email}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Phone Number: </span>
                        <span className="large-card__value">{responder.phone}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Role: </span>
                        <span className="large-card__value">{getEnumLabelByValue(RESPONDER_ROLES_CONST, responder.role)}</span>
                    </div>
                    <div>
                        <span className="large-card__label">US&R Team: </span>
                        <span className="large-card__value">{getEnumLabelByValue(USR_TEAMS, responder.usarTeamNumber)}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Team Leader: </span>
                        <span className="large-card__value">{responder.isTeamLeader ? 'Yes' : 'No'}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Meal Access: </span>
                        <span className="large-card__value">{getEnumLabelByValue(MEAL_ACCESS_CONST, responder.mealAccess)}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Incident Notification Date: </span>
                        <span className="large-card__value">{formatToLocalTime(responder.incidentNotificationDate, 'date')}</span>
                    </div>
                    <div>
                        <span className="large-card__label">First Travel Day: </span>
                        <span className="large-card__value">{formatToLocalTime(responder.firstTravelDay, 'date')}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Check In Date: </span>
                        <span className="large-card__value">{formatToLocalTime(responder.checkInDate, 'date')}</span>
                    </div>
                    <div>
                        <span className="large-card__label">First Day of Work: </span>
                        <span className="large-card__value">{formatToLocalTime(responder.firstDayOfWork, 'date')}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Anticipated Last Day of Work: </span>
                        <span className="large-card__value">{formatToLocalTime(responder.anticipatedLastDayOfWork, 'date')}</span>
                    </div>
                    <div>
                        <span className="large-card__label">Check Out Date: </span>
                        <span className="large-card__value">{formatToLocalTime(responder.checkOutDate, 'date')}</span>
                    </div>
                    
                    {showDeploymentFields && (
                        <>
                            <div>
                                <span className="large-card__label">Assigned Location: </span>
                                <span className="large-card__value">{responder.locationName ?? '-'}</span>
                            </div>
                            <div>
                                <span className="large-card__label">Status: </span>
                                <span className="large-card__value">{getEnumLabelByValue(RESPONDER_STATUS_CONST, responder.status)}</span>
                            </div>
                        </>
                    )}
                </div>

                <div className="large-card__section">
                    <div className="license-image">
                        <LazyImage
                            src={responder.licenseImageUrl}
                            fallback={<div className="license-image--no-image" />}
                        />
                    </div>
                    <div className="person-image">
                        <LazyImage
                            src={responder.personalImageUrl}
                            fallback={<div className="person-image--no-image" />}
                        />
                    </div>
                </div>
            </div>
            
            {actions && (
                <div className="large-card__actions">
                    {actions}
                </div>
            )}
        </div>
    );
};

export default ResponderCard;
